import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import moment from 'moment';

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Clock,
  MapPin,
  Calendar as CalendarIcon,
  Play,
  Square,
  MessageSquare,
  Camera,
  AlertCircle,
  CheckCircle2,
  History,
  FileText
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import PhotoUpload from "@/components/PhotoUpload";
import { JobChat } from "@/components/JobChat";

export default function MySchedulePage() {
  const { user } = useContext(AuthContext);
  const [schedules, setSchedules] = useState([]);
  const [currentTimeEntry, setCurrentTimeEntry] = useState(null);
  const [timeHistory, setTimeHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("schedule");
  const [schedulePhotos, setSchedulePhotos] = useState([]);
  const [customerNotes, setCustomerNotes] = useState([]);
  
  // Modal states
  const [showClockOutModal, setShowClockOutModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  
  // Form states
  const [clockOutData, setClockOutData] = useState({ break_minutes: 0, notes: "" });
  const [newNote, setNewNote] = useState({ content: "", note_type: "text" });
  const [jobNotes, setJobNotes] = useState([]);
  
  // Timer for active clock
  const [elapsedTime, setElapsedTime] = useState(0);

  const isEmployee = user?.role === 'employee';

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let interval;
    if (currentTimeEntry) {
      interval = setInterval(() => {
        const clockIn = moment(currentTimeEntry.clock_in);
        const now = moment();
        setElapsedTime(now.diff(clockIn, 'seconds'));
      }, 1000);
    }
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTimeEntry]);

  const fetchData = async () => {
    try {
      const requests = [axios.get(`${API}/schedules`).catch(() => ({ data: [] }))];
      
      if (isEmployee) {
        requests.push(axios.get(`${API}/timecard/current`).catch(() => ({ data: null })));
        requests.push(axios.get(`${API}/timecard/history`).catch(() => ({ data: [] })));
      }
      
      const responses = await Promise.all(requests);
      setSchedules(responses[0].data || []);
      
      if (isEmployee) {
        setCurrentTimeEntry(responses[1].data);
        setTimeHistory(responses[2].data);
      }
    } catch (error) {
      toast.error("Failed to fetch schedule");
    } finally {
      setLoading(false);
    }
  };

  const getLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) { resolve(null); return; }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => resolve(null),
        { timeout: 10000, enableHighAccuracy: true }
      );
    });
  };

  const handleClockIn = async (schedule) => {
    try {
      toast.loading("Getting location...");
      const loc = await getLocation();
      toast.dismiss();
      const response = await axios.post(`${API}/timecard/clock-in`, {
        schedule_id: schedule.schedule_id,
        project_id: schedule.project_id,
        notes: `Started work on: ${schedule.title}`,
        latitude: loc?.latitude,
        longitude: loc?.longitude,
      });
      setCurrentTimeEntry(response.data);
      toast.success(loc ? "Clocked in with location!" : "Clocked in (location unavailable)");
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || "Failed to clock in");
    }
  };

  const handleClockOut = async () => {
    try {
      toast.loading("Getting location...");
      const loc = await getLocation();
      toast.dismiss();
      const response = await axios.post(`${API}/timecard/clock-out`, {
        ...clockOutData,
        latitude: loc?.latitude,
        longitude: loc?.longitude,
      });
      setCurrentTimeEntry(null);
      setTimeHistory([response.data, ...timeHistory]);
      setShowClockOutModal(false);
      setClockOutData({ break_minutes: 0, notes: "" });
      toast.success(`Clocked out! Total: ${response.data.total_hours} hours`);
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || "Failed to clock out");
    }
  };

  const handleViewSchedule = async (schedule) => {
    setSelectedSchedule(schedule);
    try {
      const [notesRes, photosRes, custNotesRes] = await Promise.all([
        schedule.project_id 
          ? axios.get(`${API}/job-board/${schedule.project_id}`).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
        axios.get(`${API}/photos?schedule_id=${schedule.schedule_id}`).catch(() => ({ data: [] })),
        schedule.project_id
          ? axios.get(`${API}/projects/${schedule.project_id}/customer-notes`).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] })
      ]);
      setJobNotes(notesRes.data || []);
      setSchedulePhotos(photosRes.data || []);
      setCustomerNotes(custNotesRes.data || []);
    } catch (error) {
      // Error handled silently — non-critical operation
    }
  };

  const handlePhotoUploadComplete = (photo) => {
    setSchedulePhotos([photo, ...schedulePhotos]);
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!selectedSchedule) return;
    
    try {
      const response = await axios.post(`${API}/job-board`, {
        project_id: selectedSchedule.project_id,
        content: newNote.content,
        post_type: newNote.note_type || "note"
      });
      setJobNotes([response.data, ...jobNotes]);
      setNewNote({ content: "", note_type: "text" });
      setShowNoteModal(false);
      toast.success("Note added!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add note");
    }
  };

  const formatElapsedTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const todaySchedules = schedules.filter(s => s.date === moment().format('YYYY-MM-DD'));
  const upcomingSchedules = schedules.filter(s => s.date > moment().format('YYYY-MM-DD'));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="my-schedule-loading">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-schedule-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            My Schedule
          </h1>
          <p className="text-slate-600 mt-1">
            {todaySchedules.length} jobs today • {upcomingSchedules.length} upcoming
          </p>
        </div>
      </div>

      {/* Active Clock Card */}
      {isEmployee && currentTimeEntry && (
        <Card className="border-green-200 bg-green-50" data-testid="active-clock-card">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white animate-pulse" />
                </div>
                <div>
                  <p className="text-sm text-green-700">Currently Working</p>
                  <p className="text-2xl font-bold text-green-800 font-mono">
                    {formatElapsedTime(elapsedTime)}
                  </p>
                  {currentTimeEntry.project && (
                    <p className="text-sm text-green-600">{currentTimeEntry.project.name}</p>
                  )}
                </div>
              </div>
              <Button 
                variant="destructive"
                onClick={() => setShowClockOutModal(true)}
                data-testid="clock-out-btn"
              >
                <Square className="w-4 h-4 mr-2" />
                Clock Out
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="schedule" data-testid="tab-schedule">
            <CalendarIcon className="w-4 h-4 mr-2" />
            Schedule
          </TabsTrigger>
          {isEmployee && (
            <TabsTrigger value="timecard" data-testid="tab-timecard">
              <History className="w-4 h-4 mr-2" />
              Timecard
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="schedule" className="mt-6">
          {/* Today's Jobs */}
          {todaySchedules.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                Today
              </h2>
              <div className="grid gap-4">
                {todaySchedules.map(schedule => (
                  <ScheduleCard 
                    key={schedule.schedule_id}
                    schedule={schedule}
                    isEmployee={isEmployee}
                    isClockedIn={currentTimeEntry?.schedule_id === schedule.schedule_id}
                    canClockIn={isEmployee && !currentTimeEntry}
                    onClockIn={() => handleClockIn(schedule)}
                    onViewDetails={() => handleViewSchedule(schedule)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Upcoming Jobs */}
          {upcomingSchedules.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                Upcoming
              </h2>
              <div className="grid gap-4">
                {upcomingSchedules.map(schedule => (
                  <ScheduleCard 
                    key={schedule.schedule_id}
                    schedule={schedule}
                    isEmployee={isEmployee}
                    onViewDetails={() => handleViewSchedule(schedule)}
                  />
                ))}
              </div>
            </div>
          )}

          {schedules.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">No scheduled jobs</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {isEmployee && (
          <TabsContent value="timecard" className="mt-6">
            <Card data-testid="timecard-history">
              <CardHeader>
                <CardTitle>Time History</CardTitle>
                <CardDescription>Your recent clock in/out records</CardDescription>
              </CardHeader>
              <CardContent>
                {timeHistory.length > 0 ? (
                  <div className="space-y-4">
                    {timeHistory.map(entry => (
                      <div 
                        key={entry.entry_id}
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: entry.project?.color + '20' || '#f1f5f9' }}
                          >
                            <Clock className="w-5 h-5" style={{ color: entry.project?.color || '#64748b' }} />
                          </div>
                          <div>
                            <p className="font-medium">{entry.project?.name || 'General'}</p>
                            <p className="text-sm text-slate-500">
                              {moment(entry.clock_in).format('ddd, MMM D')} • 
                              {moment(entry.clock_in).format('h:mm A')} - {entry.clock_out ? moment(entry.clock_out).format('h:mm A') : 'Active'}
                            </p>
                            {entry.clock_in_location && (
                              <a
                                href={`https://www.google.com/maps?q=${entry.clock_in_location.lat},${entry.clock_in_location.lng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-500 hover:underline flex items-center gap-1 mt-0.5"
                              >
                                <MapPin className="w-3 h-3" /> View clock-in location
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-semibold">{entry.total_hours}h</p>
                          <Badge 
                            className={
                              entry.status === 'approved' ? 'bg-green-100 text-green-700' :
                              entry.status === 'rejected' ? 'bg-red-100 text-red-700' :
                              entry.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            }
                          >
                            {entry.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No time entries yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Schedule Detail Modal */}
      <Dialog open={!!selectedSchedule} onOpenChange={() => setSelectedSchedule(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedSchedule?.title}</DialogTitle>
          </DialogHeader>
          {selectedSchedule && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Date</Label>
                  <p className="font-medium">{moment(selectedSchedule.date).format('ddd, MMM D, YYYY')}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Time</Label>
                  <p className="font-medium">{selectedSchedule.start_time} - {selectedSchedule.end_time}</p>
                </div>
              </div>

              {selectedSchedule.project && (
                <div>
                  <Label className="text-slate-500">Project</Label>
                  <p className="font-medium">{selectedSchedule.project.name}</p>
                  {selectedSchedule.project.address && (
                    <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      {selectedSchedule.project.address}
                    </p>
                  )}
                </div>
              )}

              {selectedSchedule.description && (
                <div>
                  <Label className="text-slate-500">Description</Label>
                  <p>{selectedSchedule.description}</p>
                </div>
              )}

              {selectedSchedule.notes && (
                <div>
                  <Label className="text-slate-500">Notes from Manager</Label>
                  <p className="text-sm bg-slate-50 p-3 rounded-lg">{selectedSchedule.notes}</p>
                </div>
              )}

              {/* Customer Notes - Important Instructions */}
              {customerNotes.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <Label className="text-amber-800 flex items-center gap-2 mb-3">
                    <AlertCircle className="w-4 h-4" />
                    Customer Instructions
                  </Label>
                  <ul className="space-y-2">
                    {customerNotes.map(note => (
                      <li key={note.post_id || note.note_id} className="flex items-start gap-2 text-amber-900">
                        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                        <span className="text-sm">{note.content}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Job Notes Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-slate-500">Job Notes</Label>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowNoteModal(true)}
                    data-testid="add-note-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Add Note
                  </Button>
                </div>
                {jobNotes.length > 0 ? (
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {jobNotes.map(note => (
                      <div key={note.post_id || note.note_id} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={note.author?.picture} />
                          <AvatarFallback className="text-xs">
                            {note.author?.name?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{note.author?.name}</span>
                            <span className="text-xs text-slate-400">
                              {moment(note.created_at).fromNow()}
                            </span>
                            {(note.post_type || note.note_type) === 'issue' && (
                              <Badge className="bg-red-100 text-red-700 text-xs">Issue</Badge>
                            )}
                          </div>
                          <p className="text-sm mt-1">{note.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">No notes yet</p>
                )}
              </div>

              {/* Photos Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-slate-500">Job Photos</Label>
                  <PhotoUpload 
                    scheduleId={selectedSchedule.schedule_id}
                    projectId={selectedSchedule.project_id}
                    onUploadComplete={handlePhotoUploadComplete}
                  />
                </div>
                {schedulePhotos.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                    {schedulePhotos.map(photo => (
                      <div key={photo.photo_id} className="relative group">
                        <img
                          src={`${API}${photo.url}`}
                          alt={photo.caption || "Job photo"}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        {photo.caption && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 rounded-b-lg truncate">
                            {photo.caption}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">No photos yet</p>
                )}
              </div>

              {/* Job Chat */}
              {selectedSchedule.project_id && (
                <div className="border rounded-lg overflow-hidden">
                  <JobChat projectId={selectedSchedule.project_id} scheduleId={selectedSchedule.schedule_id} compact />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Clock Out Modal */}
      <Dialog open={showClockOutModal} onOpenChange={setShowClockOutModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Clock Out</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-center py-4">
              <p className="text-3xl font-bold font-mono text-slate-900">
                {formatElapsedTime(elapsedTime)}
              </p>
              <p className="text-slate-500 mt-1">Total time worked</p>
            </div>
            
            <div>
              <Label>Break Time (minutes)</Label>
              <Input
                type="number"
                value={clockOutData.break_minutes}
                onChange={(e) => setClockOutData({ ...clockOutData, break_minutes: parseInt(e.target.value) || 0 })}
                placeholder="0"
                min="0"
                data-testid="break-minutes"
              />
            </div>
            
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={clockOutData.notes}
                onChange={(e) => setClockOutData({ ...clockOutData, notes: e.target.value })}
                placeholder="Any notes about your work..."
                rows={3}
                data-testid="clock-out-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClockOutModal(false)}>Cancel</Button>
            <Button onClick={handleClockOut} data-testid="confirm-clock-out">
              Confirm Clock Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Modal */}
      <Dialog open={showNoteModal} onOpenChange={setShowNoteModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Job Note</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddNote} className="space-y-4">
            <div>
              <Label>Note Type</Label>
              <div className="flex gap-2 mt-2">
                {[
                  { value: 'text', label: 'Note', icon: FileText },
                  { value: 'issue', label: 'Issue', icon: AlertCircle },
                ].map(type => (
                  <Button
                    key={type.value}
                    type="button"
                    variant={newNote.note_type === type.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => setNewNote({ ...newNote, note_type: type.value })}
                  >
                    <type.icon className="w-4 h-4 mr-1" />
                    {type.label}
                  </Button>
                ))}
              </div>
            </div>
            
            <div>
              <Label>Content *</Label>
              <Textarea
                value={newNote.content}
                onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                placeholder="Describe what you observed or any issues..."
                rows={4}
                required
                data-testid="note-content"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNoteModal(false)}>Cancel</Button>
              <Button type="submit" disabled={!newNote.content} data-testid="save-note-btn">
                Save Note
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Schedule Card Component
function ScheduleCard({ schedule, isEmployee, isClockedIn, canClockIn, onClockIn, onViewDetails }) {
  return (
    <Card 
      className={`cursor-pointer hover:shadow-md transition-shadow ${isClockedIn ? 'border-green-500 bg-green-50' : ''}`}
      onClick={onViewDetails}
      data-testid={`schedule-card-${schedule.schedule_id}`}
    >
      <CardContent className="py-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div 
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: schedule.project?.color || '#6366f1' }}
            >
              <CalendarIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">{schedule.title}</h3>
              {schedule.project && (
                <p className="text-sm text-slate-600">{schedule.project.name}</p>
              )}
              <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {schedule.start_time} - {schedule.end_time}
                </span>
                {schedule.project?.address && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {schedule.project.address.split(',')[0]}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isClockedIn && (
              <Badge className="bg-green-100 text-green-700">
                <Clock className="w-3 h-3 mr-1" />
                Working
              </Badge>
            )}
            {canClockIn && (
              <Button 
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onClockIn();
                }}
                data-testid={`clock-in-${schedule.schedule_id}`}
              >
                <Play className="w-4 h-4 mr-1" />
                Clock In
              </Button>
            )}
            {schedule.is_published && (
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
